// Who this sign-in is. Nothing on this page belongs to a show.
//
// ====================================================== WHAT IS NOT ON IT, AND WHY
//
// The design pack's account page has nine facts on it and this console can
// honestly answer four. The gap is not laziness and it is not caution: there is
// no endpoint that answers the other five, and every one of them would have to
// be invented to be drawn. They are listed here rather than in a report nobody
// will find, because the person most likely to add one of them back is somebody
// reading this file with the pack open beside it.
//
//   A NAME. `identity` in the database has `identity_id`, `email`, `role`,
//   `client_id`, `created_at`, `created_by` and `disabled_at`. There is no
//   column for what a person is called, nothing on the sign-in path asks, and
//   no route would return it. The pack shows a Name field with a "Save your
//   name" button beside it; a field here would be a text box that discards what
//   is typed into it, and a disabled one would be a promise. It is absent.
//
//   ON THIS SERVICE SINCE. `identity.created_at` exists and is exactly the
//   right value. Nothing exposes it: AUTH_ENDPOINTS is six urls, none of them a
//   GET, and api/src/auth/routes.ts argues at length against adding one — "a
//   `GET /v1/auth/session` answering this for a bearer token would answer
//   somebody who found a token, and would do it without the report path's cost
//   of leaving a row in report_access". So the data is one column away and the
//   route to it is a decision nobody has made. The row is omitted rather than
//   filled with the session's own age, which would be a different fact wearing
//   this one's label.
//
//   LAST SIGNED IN. `auth_session.last_seen_at` exists, same story, same
//   absence of a route. What this console does know is when the session in this
//   tab stops being one, which is a different fact and is drawn under its own
//   name.
//
//   WHERE YOU ARE SIGNED IN, and RECENT SIGN-INS. NO LONGER ABSENT, and the way
//   they arrived is the interesting part. This block used to say both "would
//   need a route that reads sessions back, which is the specific thing routes.ts
//   refuses" — and that refusal was not overruled, it was ANSWERED. Its two
//   objections were that such a route "would answer somebody who found a token"
//   and would leave no audit trail. AUTH_ENDPOINTS.listSessions requires a
//   second factor proved ON THE REQUEST rather than inherited from the sign-in,
//   which is what a found token cannot supply, and each call writes a log line.
//   See SignIns.tsx and api/signIns.ts.
//
//   THE TWO SECTIONS ARE STILL ONE, because the data cannot honestly make two.
//   Rotation updates a session row in place rather than inserting, so "recent
//   sign-ins" and "where you are signed in" read the same rows; drawing them
//   twice under different headings would be one list pretending to be two kinds
//   of evidence. There is also no address and no device on any row — 006 keeps
//   `requested_ip` on `login_code` alone and briefly, on purpose — so the table
//   says when and not where, and says so out loud.
//
//   WHAT THIS ROLE CAN DO, as the pack's six-row table. Two of its rows are
//   verifiable from this repository and four are claims about policy that
//   nothing here can check. A capability table is the worst possible place for
//   a plausible guess: somebody reads "Delete an activation — No" and stops
//   looking. What is drawn instead is the part reports/auth.ts and 004 actually
//   settle, in prose, and a sentence saying a role is Nova's to grant.
//
// ============================================================ AND THE APPEARANCE
//
// The pack says the light-or-dark choice is "kept against this sign-in, so it
// follows you to another machine". IT DOES NOT. There is no endpoint that
// stores a preference against an identity, and theme.tsx keeps the choice in
// this browser's localStorage under `xpConsoleTheme`. The wording on this page
// says that instead, which is the only wording available that is true. If a
// preference endpoint is ever added, this is one sentence and one write.

import type { ReactNode } from "react";
import { DefList, PageHead, Setting } from "@nova/desk-ui/parts";
import { SecLabel } from "@nova/desk-ui/kit";
import type { Session, SessionToken } from "../session/store.js";
import type { ApiOptions } from "../api/client.js";
import { ThemeToggle } from "../theme/theme.js";
import { EmailStats } from "./EmailStats.js";
import { SignIns } from "./SignIns.js";
import { roleAndScope } from "./whoami.js";
import { opens } from "./roleAccess.js";

export type AccountProps = {
  readonly session: Session;
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
};

export function Account({ session, api, token, onSessionEnded }: AccountProps): ReactNode {
  const { role, scope } = roleAndScope(session);

  // Built rather than written out, because two of the four are conditional and
  // a DefList with an empty cell in it is the placeholder this page exists to
  // avoid. A row that has nothing to say is not a row.
  const rows: [string, ReactNode][] = [["Role", role]];
  if (scope !== null) rows.push(["Scope", scope]);
  rows.push(["Signed in until", new Date(session.expiresAt).toLocaleString()]);
  rows.push([
    "Authenticator on this sign-in",
    session.secondFactorProved === true
      ? "Used"
      : session.secondFactorProved === false
        ? "Not used"
        : "This tab did not record it",
  ]);

  return (
    <div className="stack">
      <PageHead
        title="Account"
        lede="Your sign-in, access and security settings."
        explain="info"
      />

      <section>
        <SecLabel info="This is the email used to sign in. Ask Nova to change it.">
          You
        </SecLabel>
        <div className="panel panel-stack">
          <div className="fixed">
            <span className="label">Sign-in email</span>
            {session.email === null ? (
              // Said rather than left blank, and said as a fact about this tab
              // rather than about the account. A session stored before the
              // console kept the address is a perfectly good session, and an
              // empty panel under a heading reads as an account with no email.
              <p className="help">
                This older session did not keep the email address. Sign out and back in to show it here.
              </p>
            ) : (
              <>
                <p className="val">{session.email}</p>
                <p className="help">
                  Sign-in codes are sent here. Ask Nova to change the account email.
                </p>
              </>
            )}
          </div>

          <DefList rows={rows} />
        </div>
      </section>

      <SignIns api={api} token={token} onSessionEnded={onSessionEnded} />

      {opens({ role: session.role, canExport: session.canExport }, "emailStats") ? (
        <EmailStats api={api} token={token} onSessionEnded={onSessionEnded} />
      ) : null}

      <section>
        <SecLabel info="Saved in this browser only. It does not affect attendee screens.">
          Appearance
        </SecLabel>
        <div className="panel panel-stack">
          <Setting
            label="Light or dark"
            desc={
              // NOT THE PACK'S SENTENCE. Its version promises this follows you
              // to another machine, and nothing in this service could make that
              // true. See the header.
              "Saved on this device. Other devices keep their own choice. This does not change the stand."
            }
          >
            <ThemeToggle />
          </Setting>
        </div>
      </section>

      <section>
        <SecLabel>What this role can do</SecLabel>
        <div className="panel panel-stack">
          {/* Three cases, one per role the service issues (Gabe, 11 Oct). An
              Editor used to read the client viewer's sentence, which told
              Nova staff who build and publish that they could only read. A
              role this build does not know gets no sentence rather than a
              guess, as roleAndScope draws it. */}
          {session.role === "nova_admin" ? (
            <>
              <p>
                Nova admins can work across every client. Reports also require an authenticator.
              </p>
              <p>Every report opened by this account is recorded.</p>
            </>
          ) : session.role === "nova_editor" ? (
            <>
              <p>
                Editors build and publish activations for the clients they are given. Each publish asks
                for a fresh authenticator code.
              </p>
              <p>Every report opened by this account is recorded.</p>
            </>
          ) : session.role === "client_viewer" ? (
            <p>
              Client viewers can read one client&rsquo;s activations.
            </p>
          ) : null}
          <p className="help">
            Nova manages roles. They cannot be changed here.
          </p>
        </div>
      </section>

      <section>
        <SecLabel>Disable this account</SecLabel>
        <div className="panel panel-stack">
          <p>
            Disabling an account does not delete its activations, exports or audit history.
          </p>
          <p className="help">
            {/* Prose and no button. The pack has one, and here it would be a
                control that sends nothing anywhere: `identity.disabled_at` is
                set by the service and there is no route a browser could ask
                through. A button that only opens a mail client is a button that
                does nothing on a machine with no mail client set up. */}
            Ask Nova to disable the account.
          </p>
        </div>
      </section>
    </div>
  );
}
